import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { RemoteCaptureData, RemoteField, SessionStatus } from './remote-capture.service';

export interface WebSocketMessage {
  type: 'session_status' | 'file_data' | 'session_created' | 'session_ended' | 'session_terminated' | 'get_active_sessions' | 'validation_response';
  sessionId: string;
  computerId: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private isConnecting = false;

  // Observables para diferentes tipos de mensajes
  private sessionStatusSubject = new BehaviorSubject<SessionStatus | null>(null);
  private fileDataSubject = new BehaviorSubject<RemoteCaptureData | null>(null);
  private validationResponseSubject = new BehaviorSubject<any>(null);
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);

  constructor() {
    // CONECTAR AUTOMÁTICAMENTE si WebSocket está habilitado
    if (environment.websocket?.enabled) {
      this.connect();
    }
  }

  private connect() {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;

    try {
      const wsUrl = this.getWebSocketUrl();

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.connectionStatusSubject.next(true);

        // LIMPIAR ESTADO OBSOLETO al reconectar
        this.sessionStatusSubject.next(null);
        this.fileDataSubject.next(null);

        // Enviar mensajes pendientes
        this.sendPendingMessages();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          // Error parsing WebSocket message
        }
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        this.connectionStatusSubject.next(false);

        // Intentar reconectar si no fue un cierre intencional
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        this.isConnecting = false;
      };

    } catch (error) {
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host = window.location.hostname;

    let wsUrl: string;

    // Verificar si estamos en desarrollo local (environment.ts)
    const isLocalDevelopment = environment.websocket?.host === 'localhost' &&
                              String(environment.websocket?.port) === '3000' &&
                              environment.websocket?.protocol === 'ws';

    if (isLocalDevelopment) {
      // DESARROLLO LOCAL: Usar lógica dinámica para red local
      if (host === 'localhost' || host === '127.0.0.1') {
        wsUrl = `${protocol}://${host}:3000/remote-capture`;
      } else {
        // Para acceso desde red local (IP local), usar el mismo host
        wsUrl = `${protocol}://${host}:3000/remote-capture`;
      }
    } else {
      // PRODUCCIÓN/AZURE: Usar configuración del environment
      if (environment.websocket?.enabled && environment.websocket?.host) {
        const envProtocol = environment.websocket.protocol || 'wss';
        const envHost = environment.websocket.host;
        const envPort = environment.websocket.port;

        // Si el puerto es 80 o 443, no incluirlo en la URL
        const portSuffix = (String(envPort) === '80' || String(envPort) === '443') ? '' : `:${envPort}`;

        wsUrl = `${envProtocol}://${envHost}${portSuffix}/remote-capture`;
      } else {
        // Fallback para otros hosts
        wsUrl = `${protocol}://${host}:3000/remote-capture`;
      }
    }

    return wsUrl;
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      // Backoff exponencial: 1s, 2s, 4s, 8s, 16s...
      const backoffDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);

      timer(backoffDelay).subscribe(() => {
        this.connect();
      });
    }
  }

    private handleMessage(message: WebSocketMessage) {
    switch (message.type) {
      case 'session_status':
        this.sessionStatusSubject.next(message.data);
        break;

      case 'file_data':
        this.fileDataSubject.next(message.data);
        break;

      case 'session_created':
        // Notificar que se creó una sesión
        const createdStatus = {
          active: true,
          message: 'Sesión activa',
          fields: message.data.fields,
          sessionId: message.sessionId
        };
        this.sessionStatusSubject.next(createdStatus);
        break;

      case 'session_ended':
        // Notificar que terminó una sesión
        const endedStatus = {
          active: false,
          message: 'Sesión terminada',
          sessionId: message.sessionId
        };
        this.sessionStatusSubject.next(endedStatus);
        break;

      case 'session_terminated':
        // Notificar que se terminó una sesión
        const terminatedStatus = {
          active: false,
          message: 'Sesión terminada',
          sessionId: message.sessionId
        };
        this.sessionStatusSubject.next(terminatedStatus);
        break;

      case 'validation_response':
        // Notificar respuesta de validación
        this.validationResponseSubject.next(message.data);
        break;

      default:
        // Tipo de mensaje no reconocido
        break;
    }
  }

  // Métodos públicos
  connectIfNeeded(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Solo conectar si no estamos ya intentando conectar
      if (!this.isConnecting) {
        this.connect();
      }
    }
  }

  sendMessage(message: WebSocketMessage) {
    // Solo conectar si no estamos ya conectados
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.connectIfNeeded();
      // Guardar mensaje para enviar después de conectar
      this.saveMessageToLocalStorage(message);
      return;
    }

    // Enviar mensaje si estamos conectados
    this.ws.send(JSON.stringify(message));
  }

  private saveMessageToLocalStorage(message: WebSocketMessage) {
    const storageKey = `websocket_message_${message.sessionId}_${Date.now()}`;
    localStorage.setItem(storageKey, JSON.stringify(message));
  }

  private sendPendingMessages() {
    // Buscar mensajes pendientes en localStorage
    const keys = Object.keys(localStorage);
    const pendingKeys = keys.filter(key => key.startsWith('websocket_message_'));

    pendingKeys.forEach(key => {
      try {
        const messageData = localStorage.getItem(key);
        if (messageData) {
          const message = JSON.parse(messageData);
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
            localStorage.removeItem(key); // Eliminar mensaje enviado
          }
        }
      } catch (error) {
        localStorage.removeItem(key); // Eliminar mensaje corrupto
      }
    });
  }

  // Crear sesión
  createSession(sessionId: string, computerId: string, fields?: RemoteField[]) {
    const message: WebSocketMessage = {
      type: 'session_created',
      sessionId,
      computerId,
      data: {
        fields,
        timestamp: new Date()
      }
    };
    this.sendMessage(message);
  }

  // Enviar archivo
  sendFile(sessionId: string, computerId: string, fileData: RemoteCaptureData) {
    const message: WebSocketMessage = {
      type: 'file_data',
      sessionId,
      computerId,
      data: fileData
    };
    this.sendMessage(message);
  }

  // Terminar sesión
  endSession(sessionId: string, computerId: string) {
    const message: WebSocketMessage = {
      type: 'session_ended',
      sessionId,
      computerId,
      data: {
        timestamp: new Date()
      }
    };
    this.sendMessage(message);
  }

  // Solicitar sesiones activas
  requestActiveSessions(computerId: string) {
            const message: WebSocketMessage = {
          type: 'get_active_sessions',
          sessionId: 'query', // No es una sesión real, solo para consulta
          computerId,
          data: {}
        };
    this.sendMessage(message);
  }

  // Observables
  getSessionStatusObservable(): Observable<SessionStatus | null> {
    return this.sessionStatusSubject.asObservable();
  }

  getFileDataObservable(): Observable<RemoteCaptureData | null> {
    return this.fileDataSubject.asObservable();
  }

  /** Evita re-emitir el último archivo al suscribirse de nuevo (BehaviorSubject). */
  clearFileData(): void {
    this.fileDataSubject.next(null);
  }

  getValidationResponseObservable(): Observable<any> {
    return this.validationResponseSubject.asObservable();
  }

  getConnectionStatusObservable(): Observable<boolean> {
    return this.connectionStatusSubject.asObservable();
  }

  // Verificar conexión
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // Cerrar conexión
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Cierre intencional');
      this.ws = null;
    }
  }
}
